import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import MaintenanceModal, { MAINTENANCE_MODE } from './components/MaintenanceModal';
import FiveMillionCelebration from './components/FiveMillionCelebration';
import { ADMIN_LOGIN_PATH } from './constants/admin';
import './App.css';

// ============================================
// 1. PAGE IMPORTS (Lazy Loading for Performance)
// ============================================

// Public Pages (No Login Required)
import HomePage from './pages/HomePage';
import AboutPage from './pages/AboutPage';
import PrivacyPage from './pages/PrivacyPage';
import TermsPage from './pages/TermsPage';
import RefundPage from './pages/RefundPage';
import CodeOfConductPage from './pages/CodeOfConductPage';
import FAQPage from './pages/FAQPage';
import ContactPage from './pages/ContactPage';
import HelpCenter from './pages/HelpCenter';
import CityPage from './pages/CityPage';
import DeleteAccount from './pages/DeleteAccount';
import NotFoundPage from './pages/NotFoundPage';

// Admin Pages
const AdminLogin = lazy(() => import('./pages/AdminLogin'));
import AdminForgotPassword from './pages/AdminForgotPassword';
const AdminPanel = lazy(() => import('./pages/AdminPanel'));

// Protected Pages (Login Required)
const Dashboard = lazy(() => import('./pages/Dashboard'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const ProfileTab = lazy(() => import('./pages/account/ProfileTab'));
const PasswordTab = lazy(() => import('./pages/account/PasswordTab'));
const DocumentsTab = lazy(() => import('./pages/account/DocumentsTab'));
const MembershipTab = lazy(() => import('./pages/account/MembershipTab'));
const FindKoPartner = lazy(() => import('./pages/FindKoPartner'));
const MyBookings = lazy(() => import('./pages/MyBookings'));
const BookingChat = lazy(() => import('./pages/BookingChat'));
const MembershipPage = lazy(() => import('./pages/MembershipPage'));
const ServicesPage = lazy(() => import('./pages/ServicesPage'));
const PaymentStatus = lazy(() => import('./pages/PaymentStatus'));
const TransactionHistory = lazy(() => import('./pages/TransactionHistory'));
const KoPartnerProfile = lazy(() => import('./pages/KoPartnerProfile'));
const EmailVerified = lazy(() => import('./pages/EmailVerified'));
const SetPassword = lazy(() => import('./components/SetPassword'));

// ============================================
// 2. LOADING COMPONENT
// ============================================
const Loader = () => (
  <div className="min-h-screen flex items-center justify-center bg-gray-50">
    <div className="text-center">
      <div className="animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mx-auto"></div>
      <p className="text-gray-500 mt-3">Loading...</p>
    </div>
  </div>
);

// ============================================
// 3. AUTH GUARD (Protected Route) - ✅ FIXED
// ============================================
const ProtectedRoute = ({ children, adminOnly = false }) => {
  const { user, loading } = useAuth();
  const adminToken = localStorage.getItem('admin_token');

  if (loading) return <Loader />;

  // ✅ AGAR ADMIN ROUTE HAI - Token check karo
  if (adminOnly) {
    if (!adminToken) {
      return <NotFoundPage />;
    }
    return <Suspense fallback={<Loader />}>{children}</Suspense>;
  }

  // ✅ NORMAL USER ROUTES - User check karo
  if (!user) {
    return <Navigate to="/" replace />;
  }

  return <Suspense fallback={<Loader />}>{children}</Suspense>;
};

// ============================================
// 4. MAIN APP COMPONENT
// ============================================
const SecretAdminLogin = () => {
  // Browser URL as typed — no case rewrite, no redirect
  if (window.location.pathname !== ADMIN_LOGIN_PATH) {
    return <NotFoundPage />;
  }
  return <AdminLogin />;
};

function App() {
  return (
    <AuthProvider>
      <MaintenanceModal isOpen={MAINTENANCE_MODE} />

      <BrowserRouter>
        <FiveMillionCelebration />
        <Suspense fallback={<Loader />}>
          <Routes>

            {/* ========== PUBLIC ROUTES ========== */}
            <Route path="/" element={<HomePage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/refund" element={<RefundPage />} />
            <Route path="/code-of-conduct" element={<CodeOfConductPage />} />
            <Route path="/faq" element={<FAQPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/kopartner-:citySlug" element={<CityPage />} />

            {/* ✅ DELETE ACCOUNT - PUBLIC ROUTE (No Login Required) */}
            <Route path="/delete" element={<DeleteAccount />} />

            {/* ========== ADMIN ROUTES ========== */}
            {/* Admin Forgot Password - Public */}
            <Route path="/admin-forgot-password" element={<AdminForgotPassword />} />

            {/* ✅ Admin Panel - PROTECTED with adminOnly flag */}
            <Route path="/admin/*" element={
              <ProtectedRoute adminOnly={true}>
                <AdminPanel />
              </ProtectedRoute>
            } />
            
            <Route path="/admin" element={
              <ProtectedRoute adminOnly={true}>
                <AdminPanel />
              </ProtectedRoute>
            } />

            {/* ========== PROTECTED ROUTES ========== */}
            {/* Account Section */}
            <Route path="/account" element={
              <ProtectedRoute>
                <AccountPage />
              </ProtectedRoute>
            }>
              <Route path="profile" element={<ProfileTab />} />
              <Route path="password" element={<PasswordTab />} />
              <Route path="documents" element={<DocumentsTab />} />
              <Route path="membership" element={<MembershipTab />} />
            </Route>

            {/* Individual Protected Pages */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            
            <Route path="/find-kopartner" element={
              <ProtectedRoute>
                <FindKoPartner />
              </ProtectedRoute>
            } />
            
            <Route path="/bookings" element={
              <ProtectedRoute>
                <MyBookings />
              </ProtectedRoute>
            } />

            <Route path="/bookings/:bookingId/chat" element={
              <ProtectedRoute>
                <BookingChat />
              </ProtectedRoute>
            } />
            
            <Route path="/membership" element={
              <ProtectedRoute>
                <MembershipPage />
              </ProtectedRoute>
            } />
            
            <Route path="/services" element={
              <ProtectedRoute>
                <ServicesPage />
              </ProtectedRoute>
            } />
            
            <Route path="/payment-status" element={
              <ProtectedRoute>
                <PaymentStatus />
              </ProtectedRoute>
            } />
            
            <Route path="/transactions" element={
              <ProtectedRoute>
                <TransactionHistory />
              </ProtectedRoute>
            } />
            
            <Route path="/kopartner/:userId" element={
              <ProtectedRoute>
                <KoPartnerProfile />
              </ProtectedRoute>
            } />
            
            <Route path="/set-password" element={
              <ProtectedRoute>
                <SetPassword />
              </ProtectedRoute>
            } />
            
            <Route path="/verify-email" element={<EmailVerified />} />

            {/* ========== REDIRECTS ========== */}
            <Route path="/cuddlist-setup" element={<Navigate to="/account/profile" replace />} />
            <Route path="/kopartner-setup" element={<Navigate to="/dashboard" replace />} />
            <Route path="/book-services" element={<Navigate to="/find-kopartner" replace />} />
            <Route path="/find-cuddlist" element={<Navigate to="/find-kopartner" replace />} />

            {/* Exact /KoPartnerAdmin → login; anything else unknown → 404 (no URL rewrite) */}
            <Route path="*" element={<SecretAdminLogin />} />

          </Routes>
        </Suspense>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
